import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getProfiles, getProfile, deleteProfile } from '../services/adminApi';

function ProfileList() {
  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchProfiles = async (page = 1) => {
    setLoading(true);
    try {
      const data = await getProfiles(page, 20, search);
      setProfiles(data.profiles);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [search]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(`Profil ${id} wirklich löschen?`)) return;

    try {
      await deleteProfile(id);
      fetchProfiles(pagination.page);
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Profile</h1>
        <div className="flex gap-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
          />
        </div>
      </div>

      <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Laden...</div>
        ) : profiles.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Keine Profile gefunden</div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5">
              <tr className="text-left text-gray-400 text-sm">
                <th className="p-4 font-medium">ID</th>
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium">Completion</th>
                <th className="p-4 font-medium">Created</th>
                <th className="p-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr
                  key={profile.id}
                  onClick={() => navigate(`/admin/profiles/${profile.id}`)}
                  className="border-t border-white/5 text-gray-300 hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <td className="p-4 font-mono text-sm">{profile.id}</td>
                  <td className="p-4">{profile.name}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      profile.status === 'complete' ? 'bg-green-500/20 text-green-300' :
                      profile.status === 'processing' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {profile.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                          style={{ width: `${profile.completion_percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{profile.completion_percentage}%</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-gray-500">
                    {profile.created_at ? new Date(profile.created_at).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={(e) => handleDelete(profile.id, e)}
                      className="px-3 py-1 text-xs bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 transition-colors"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-white/5">
            {Array.from({ length: pagination.pages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => fetchProfiles(i + 1)}
                className={`px-3 py-1 rounded ${
                  pagination.page === i + 1
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile(id);
        setProfile(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(`Profil ${id} wirklich löschen?`)) return;

    try {
      await deleteProfile(id);
      navigate('/admin/profiles');
    } catch (err) {
      alert('Löschen fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Link to="/admin/profiles" className="text-blue-400 hover:text-blue-300">
          ← Zurück zu Profile
        </Link>
        <div className="p-8 text-center text-gray-400">Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/admin/profiles" className="text-blue-400 hover:text-blue-300">
          ← Zurück zu Profile
        </Link>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-red-400">
          Fehler: {error}
        </div>
      </div>
    );
  }

  const data = profile?.data || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/profiles" className="text-blue-400 hover:text-blue-300 text-sm">
            ← Zurück zu Profile
          </Link>
          <h1 className="text-2xl font-bold text-white mt-2">Profil: {id}</h1>
        </div>
        <button
          onClick={handleDelete}
          className="px-4 py-2 bg-red-500/20 text-red-300 rounded-xl hover:bg-red-500/30 transition-colors"
        >
          Profil löschen
        </button>
      </div>

      {/* Input Values */}
      {data.input_values && (
        <Section title="Input Values">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.input_values).map(([key, value]) => (
              <DataField key={key} label={key} value={
                Array.isArray(value) ? value.join(', ') : value
              } />
            ))}
          </div>
        </Section>
      )}

      {/* Listener Profile */}
      {data.listener_profile && (
        <Section title="Listener Profile">
          {typeof data.listener_profile === 'string' ? (
            <p className="text-gray-300 whitespace-pre-wrap">{data.listener_profile}</p>
          ) : (
            <div className="space-y-2">
              {data.listener_profile.profile && (
                <p className="text-gray-300 whitespace-pre-wrap">{data.listener_profile.profile}</p>
              )}
              <pre className="text-gray-400 text-xs overflow-x-auto bg-black/30 p-3 rounded-xl mt-2">
                {JSON.stringify(data.listener_profile, null, 2)}
              </pre>
            </div>
          )}
        </Section>
      )}

      {/* Theme Recommendations */}
      {data.theme_recommendations && (
        <Section title="Theme Recommendations">
          <pre className="text-gray-300 text-sm overflow-x-auto bg-black/30 p-4 rounded-xl">
            {JSON.stringify(data.theme_recommendations, null, 2)}
          </pre>
        </Section>
      )}

      {/* Experimental Topics */}
      {data.experimental_topics && (
        <Section title="Experimental Topics">
          <pre className="text-gray-300 text-sm overflow-x-auto bg-black/30 p-4 rounded-xl">
            {JSON.stringify(data.experimental_topics, null, 2)}
          </pre>
        </Section>
      )}

      {/* Program Block */}
      {data.program_block && (
        <Section title="Program Block">
          <pre className="text-gray-300 text-sm overflow-x-auto bg-black/30 p-4 rounded-xl">
            {JSON.stringify(data.program_block, null, 2)}
          </pre>
        </Section>
      )}

      {/* Raw Data */}
      <Section title="Raw Data" collapsed>
        <pre className="text-gray-300 text-xs overflow-x-auto bg-black/30 p-4 rounded-xl max-h-96">
          {JSON.stringify(profile, null, 2)}
        </pre>
      </Section>
    </div>
  );
}

function Section({ title, children, collapsed }) {
  const [isOpen, setIsOpen] = useState(!collapsed);

  return (
    <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
      >
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <span className="text-gray-500">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
}

function DataField({ label, value }) {
  const displayValue = value === null || value === undefined ? '-' :
    typeof value === 'object' ? JSON.stringify(value) :
    String(value);

  return (
    <div>
      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-gray-300 break-words">{displayValue}</p>
    </div>
  );
}

export default function Profiles() {
  const { id } = useParams();

  return (
    <AdminLayout>
      {id ? <ProfileDetail /> : <ProfileList />}
    </AdminLayout>
  );
}
